import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (url: string, name: string) => {
    setSelectedCategory(name);
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Clean%20modern%20minimalist%20workspace%20with%20a%20laptop%20on%20a%20warm%20wooden%20desk%20surrounded%20by%20neatly%20arranged%20fashion%20items%20like%20sneakers%20hoodies%20and%20accessories%20softly%20blurred%20in%20the%20background%2C%20warm%20natural%20daylight%20from%20side%20window%2C%20cream%20beige%20and%20sage%20green%20tones%2C%20Scandinavian%20aesthetic%2C%20clean%20composition%2C%20editorial%20product%20photography%20with%20gentle%20shadows%2C%20no%20blue%20or%20purple%20colors%2C%20harmonious%20warm%20atmosphere&width=1600&height=900&seq=hero-bg-2026-x01&orientation=landscape"
          alt="CSSBuy Spreadsheet workspace with fashion product finds"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 pt-20 pb-16">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 lg:gap-16 max-w-7xl mx-auto">
          <div className="flex-1 text-center lg:text-left pt-8 lg:pt-16">
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-background-50 leading-tight tracking-tight">
              CSSBuy<br />
              <span className="text-primary-400">Spreadsheet</span><br />
              <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-background-200">Gateway</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-background-200 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              The most comprehensive CSSBuy Spreadsheet directory for 2026. Browse 15K+ verified product finds across 14 categories with instant redirects to kaspreadsheet.com.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line text-lg"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-background-50/15 text-background-50 border border-background-50/30 rounded-full text-base font-semibold hover:bg-background-50/25 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4 justify-center lg:justify-start">
              <Link
                to="/article/cssbuy-spreadsheet-guide"
                className="text-sm text-background-200/80 hover:text-background-50 transition-colors underline underline-offset-4 cursor-pointer"
              >
                Read the Complete Guide →
              </Link>
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center lg:items-end justify-center" ref={dropdownRef}>
            <div className="w-full max-w-md">
              <p className="text-sm text-background-200/80 mb-3 text-center lg:text-left font-medium tracking-wide uppercase">Browse Categories</p>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-background-50/95 backdrop-blur-sm rounded-2xl text-foreground-900 text-base font-medium border border-background-200/50 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                    {selectedCategory || 'Select a category...'}
                  </span>
                  <i className={`ri-arrow-down-s-line text-xl text-foreground-500 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-background-50 rounded-2xl shadow-lg border border-background-200/70 overflow-hidden z-50 max-h-72 overflow-y-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => handleCategorySelect(cat.url, cat.name)}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-primary-50 hover:text-foreground-900 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                      >
                        <span className="w-8 h-8 flex items-center justify-center text-primary-500">
                          <i className={`${cat.icon} text-lg`}></i>
                        </span>
                        <span className="flex-1">{cat.name}</span>
                        <span className="text-xs text-foreground-400">{cat.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}